import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { trainingAPI, driversAPI } from '../services/api';
import { toast } from 'sonner';
import {
  GraduationCap, Upload, Download, FileText, Eye, Plus, Trash2,
  CheckCircle, AlertTriangle, Clock, Search, RefreshCw, X,
  ChevronDown, ChevronRight, Camera, ClipboardCheck, BookOpen, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';

// Helper to resolve driver name from driver object
const driverName = (d) => {
  const fn = d.first_name || d.user?.first_name || '';
  const ln = d.last_name || d.user?.last_name || '';
  return `${fn} ${ln}`.trim() || d.employee_number || 'Unknown';
};

// ─── Policies Tab ────────────────────────────────────
function PoliciesSection() {
  const { isAdmin } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [showUploadTemplate, setShowUploadTemplate] = useState(null);
  const [showUploadSigned, setShowUploadSigned] = useState(null);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [filterDriver, setFilterDriver] = useState('');
  const [filterPolicy, setFilterPolicy] = useState('');
  const [newPolicyName, setNewPolicyName] = useState('');
  const [signedFile, setSignedFile] = useState(null);
  const [signedDriverId, setSignedDriverId] = useState('');
  const [signedDate, setSignedDate] = useState(new Date().toISOString().split('T')[0]);
  const [templateFile, setTemplateFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [polRes, drvRes, subRes] = await Promise.all([
        trainingAPI.listPolicies(),
        driversAPI.list(),
        trainingAPI.listPolicySubmissions(),
      ]);
      setPolicies(polRes.data);
      setDrivers(drvRes.data.filter(d => d.status === 'active' || d.is_active !== false));
      setSubmissions(subRes.data);
    } catch (e) {
      toast.error('Failed to load policies data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getLatestSubmission = (driverId, policyId) => {
    return submissions
      .filter(s => s.driver_id === driverId && s.policy_id === policyId)
      .sort((a, b) => b.submission_date.localeCompare(a.submission_date))[0];
  };

  const getStatus = (sub) => {
    if (!sub) return { label: 'Not Submitted', color: 'destructive' };
    try {
      const nextDue = new Date(sub.next_due_date);
      const now = new Date();
      const daysLeft = Math.ceil((nextDue - now) / (1000 * 60 * 60 * 24));
      if (daysLeft < 0) return { label: 'Overdue', color: 'destructive' };
      if (daysLeft <= 30) return { label: `Due in ${daysLeft}d`, color: 'warning' };
      return { label: 'Compliant', color: 'success' };
    } catch {
      return { label: 'Unknown', color: 'secondary' };
    }
  };

  const handleAddPolicy = async () => {
    if (!newPolicyName.trim()) return;
    setSubmitting(true);
    try {
      await trainingAPI.createPolicy(newPolicyName.trim());
      toast.success('Policy added');
      setNewPolicyName('');
      setShowAddPolicy(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add policy');
    }
    setSubmitting(false);
  };

  const handleUploadTemplate = async () => {
    if (!templateFile || !showUploadTemplate) return;
    setSubmitting(true);
    try {
      await trainingAPI.uploadPolicyTemplate(showUploadTemplate, templateFile);
      toast.success('Template uploaded');
      setTemplateFile(null);
      setShowUploadTemplate(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload template');
    }
    setSubmitting(false);
  };

  const handleDeletePolicy = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      await trainingAPI.deletePolicy(id);
      toast.success(`"${name}" deleted`);
      fetchData();
    } catch (e) {
      toast.error('Failed to delete policy');
    }
  };

  const handleUploadSigned = async () => {
    if (!signedFile || !signedDriverId || !showUploadSigned) return;
    setSubmitting(true);
    try {
      await trainingAPI.uploadSignedPolicy(showUploadSigned, signedDriverId, signedDate, signedFile);
      toast.success('Signed policy uploaded');
      setSignedFile(null);
      setSignedDriverId('');
      setShowUploadSigned(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload signed policy');
    }
    setSubmitting(false);
  };

  const filteredPolicies = policies.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalCombos = policies.length * drivers.length;
  const submittedCombos = new Set();
  const compliantCombos = new Set();
  drivers.forEach(d => {
    policies.forEach(p => {
      const sub = getLatestSubmission(d.id, p.id);
      if (sub) {
        submittedCombos.add(`${d.id}_${p.id}`);
        const s = getStatus(sub);
        if (s.label === 'Compliant') compliantCombos.add(`${d.id}_${p.id}`);
      }
    });
  });

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Total Policies</div>
          <div className="text-2xl font-bold" data-testid="policy-count">{policies.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Active Drivers</div>
          <div className="text-2xl font-bold">{drivers.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Submitted</div>
          <div className="text-2xl font-bold text-blue-600">{submittedCombos.size}/{totalCombos}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Compliant</div>
          <div className="text-2xl font-bold text-emerald-600">{compliantCombos.size}/{totalCombos}</div>
        </CardContent></Card>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search policies..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" data-testid="policy-search" />
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAddPolicy(true)} data-testid="add-policy-btn"><Plus className="w-4 h-4 mr-1" />Add Policy</Button>
        <Button size="sm" variant="outline" onClick={() => trainingAPI.downloadAllPolicyTemplates()} data-testid="download-all-policies-btn"><Download className="w-4 h-4 mr-1" />Download All</Button>
        <Button size="sm" variant="outline" onClick={() => setShowSubmissions(true)} data-testid="view-submissions-btn"><Eye className="w-4 h-4 mr-1" />View Submissions</Button>
        <Button size="sm" variant="outline" onClick={() => trainingAPI.exportPoliciesExcel()} data-testid="export-policies-btn"><Download className="w-4 h-4 mr-1" />Export Excel</Button>
      </div>

      {/* Policies Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy Name</TableHead>
                <TableHead>Renewal</TableHead>
                <TableHead>Template</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPolicies.map(p => (
                <TableRow key={p.id} data-testid={`policy-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.renewal_months} months</TableCell>
                  <TableCell>
                    {p.template_file ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 cursor-pointer" onClick={() => trainingAPI.downloadPolicyTemplate(p.id)}>
                        <FileText className="w-3 h-3 mr-1" />{p.template_filename || 'Download'}
                      </Badge>
                    ) : (
                      <span className="text-slate-400 text-sm">No template</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50" title="Upload Signed Policy for Driver" onClick={() => { setShowUploadSigned(p.id); setSignedFile(null); setSignedDriverId(''); }}>
                        <ClipboardCheck className="w-3.5 h-3.5 mr-1" />Signed
                      </Button>
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="outline" className="text-slate-600 border-slate-200" title="Upload/Replace Template (Admin)" onClick={() => { setShowUploadTemplate(p.id); setTemplateFile(null); }}>
                            <Upload className="w-3.5 h-3.5 mr-1" />Template
                          </Button>
                          {p.template_file && (
                            <Button size="sm" variant="ghost" title="Download Template" onClick={() => trainingAPI.downloadPolicyTemplate(p.id)}>
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" title="Delete Policy" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeletePolicy(p.id, p.name)} data-testid={`delete-policy-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredPolicies.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No policies found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Compliance Matrix */}
      {drivers.length > 0 && policies.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Driver Compliance Matrix</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-white z-10 min-w-[160px]">Driver</TableHead>
                  {policies.map(p => <TableHead key={p.id} className="text-center min-w-[120px] text-xs">{p.name}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="sticky left-0 bg-white z-10 font-medium text-sm">{driverName(d)}</TableCell>
                    {policies.map(p => {
                      const sub = getLatestSubmission(d.id, p.id);
                      const st = getStatus(sub);
                      return (
                        <TableCell key={p.id} className="text-center">
                          <Badge variant={st.color === 'success' ? 'default' : st.color === 'warning' ? 'secondary' : 'destructive'} className={`text-xs ${st.color === 'success' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}`}>
                            {st.label}
                          </Badge>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Policy Dialog */}
      <Dialog open={showAddPolicy} onOpenChange={setShowAddPolicy}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Policy</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Policy Name</Label><Input value={newPolicyName} onChange={e => setNewPolicyName(e.target.value)} placeholder="Enter policy name" data-testid="new-policy-name" /></div>
          </div>
          <DialogFooter><Button onClick={handleAddPolicy} disabled={submitting || !newPolicyName.trim()} data-testid="save-policy-btn">{submitting ? 'Saving...' : 'Add Policy'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Template Dialog */}
      <Dialog open={!!showUploadTemplate} onOpenChange={() => setShowUploadTemplate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Policy Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Template File (PDF, DOCX, etc.)</Label><Input type="file" onChange={e => setTemplateFile(e.target.files[0])} data-testid="template-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadTemplate} disabled={submitting || !templateFile} data-testid="upload-template-btn">{submitting ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Signed Policy Dialog */}
      <Dialog open={!!showUploadSigned} onOpenChange={() => setShowUploadSigned(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Signed Policy</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Driver</Label>
              <Select value={signedDriverId} onValueChange={setSignedDriverId}>
                <SelectTrigger data-testid="signed-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{driverName(d)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Submission Date</Label><Input type="date" value={signedDate} onChange={e => setSignedDate(e.target.value)} data-testid="signed-date-input" /></div>
            <div><Label>Signed Document (PDF, Image, etc.)</Label><Input type="file" onChange={e => setSignedFile(e.target.files[0])} data-testid="signed-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadSigned} disabled={submitting || !signedFile || !signedDriverId} data-testid="upload-signed-btn">{submitting ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions List Dialog */}
      <Dialog open={showSubmissions} onOpenChange={setShowSubmissions}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Policy Submissions</DialogTitle></DialogHeader>
          <div className="flex gap-3 mb-4">
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger className="max-w-[200px]"><SelectValue placeholder="All Drivers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map(d => <SelectItem key={d.id} value={d.id}>{driverName(d)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterPolicy} onValueChange={setFilterPolicy}>
              <SelectTrigger className="max-w-[200px]"><SelectValue placeholder="All Policies" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Policies</SelectItem>
                {policies.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Policy</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Next Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>File</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions
                .filter(s => (!filterDriver || filterDriver === 'all' || s.driver_id === filterDriver))
                .filter(s => (!filterPolicy || filterPolicy === 'all' || s.policy_id === filterPolicy))
                .map(s => {
                  const st = getStatus(s);
                  return (
                    <TableRow key={s.id}>
                      <TableCell>{s.driver_name}</TableCell>
                      <TableCell>{s.policy_name}</TableCell>
                      <TableCell>{s.submission_date?.slice(0, 10)}</TableCell>
                      <TableCell>{s.next_due_date?.slice(0, 10)}</TableCell>
                      <TableCell><Badge variant={st.color === 'success' ? 'default' : st.color === 'warning' ? 'secondary' : 'destructive'} className={st.color === 'success' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}>{st.label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" title="Download" onClick={() => trainingAPI.downloadSignedPolicy(s.id)}><Download className="w-4 h-4" /></Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" title="Delete" onClick={async () => {
                              if (!window.confirm(`Delete signed policy for ${s.driver_name}? This cannot be undone.`)) return;
                              try {
                                await trainingAPI.deleteSignedPolicy(s.id);
                                toast.success('Deleted');
                                fetchData();
                              } catch { toast.error('Delete failed'); }
                            }}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Reminders Tab ───────────────────────────────────
function RemindersSection() {
  const { isAdmin } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [showUploadTemplate, setShowUploadTemplate] = useState(null);
  const [showUploadSigned, setShowUploadSigned] = useState(null);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [filterDriver, setFilterDriver] = useState('');
  const [filterReminder, setFilterReminder] = useState('');
  const [newReminderName, setNewReminderName] = useState('');
  const [signedFile, setSignedFile] = useState(null);
  const [signedDriverId, setSignedDriverId] = useState('');
  const [signedDate, setSignedDate] = useState(new Date().toISOString().split('T')[0]);
  const [templateFile, setTemplateFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [remRes, drvRes, subRes] = await Promise.all([
        trainingAPI.listReminders(),
        driversAPI.list(),
        trainingAPI.listReminderSubmissions(),
      ]);
      setReminders(remRes.data);
      setDrivers(drvRes.data.filter(d => d.status === 'active' || d.is_active !== false));
      setSubmissions(subRes.data);
    } catch (e) {
      toast.error('Failed to load reminders data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getLatestSubmission = (driverId, reminderId) => {
    return submissions
      .filter(s => s.driver_id === driverId && s.reminder_id === reminderId)
      .sort((a, b) => b.submission_date.localeCompare(a.submission_date))[0];
  };

  const getStatus = (sub) => {
    if (!sub) return { label: 'Not Submitted', color: 'destructive' };
    try {
      const nextDue = new Date(sub.next_due_date);
      const now = new Date();
      const daysLeft = Math.ceil((nextDue - now) / (1000 * 60 * 60 * 24));
      if (daysLeft < 0) return { label: 'Overdue', color: 'destructive' };
      if (daysLeft <= 30) return { label: `Due in ${daysLeft}d`, color: 'warning' };
      return { label: 'Compliant', color: 'success' };
    } catch {
      return { label: 'Unknown', color: 'secondary' };
    }
  };

  const handleAddReminder = async () => {
    if (!newReminderName.trim()) return;
    setSubmitting(true);
    try {
      await trainingAPI.createReminder(newReminderName.trim());
      toast.success('Reminder added');
      setNewReminderName('');
      setShowAddReminder(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to add reminder');
    }
    setSubmitting(false);
  };

  const handleUploadTemplate = async () => {
    if (!templateFile || !showUploadTemplate) return;
    setSubmitting(true);
    try {
      await trainingAPI.uploadReminderTemplate(showUploadTemplate, templateFile);
      toast.success('Template uploaded');
      setTemplateFile(null);
      setShowUploadTemplate(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload template');
    }
    setSubmitting(false);
  };

  const handleDeleteReminder = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      await trainingAPI.deleteReminder(id);
      toast.success(`"${name}" deleted`);
      fetchData();
    } catch (e) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleUploadSigned = async () => {
    if (!signedFile || !signedDriverId || !showUploadSigned) return;
    setSubmitting(true);
    try {
      await trainingAPI.uploadSignedReminder(showUploadSigned, signedDriverId, signedDate, signedFile);
      toast.success('Signed reminder uploaded');
      setSignedFile(null);
      setSignedDriverId('');
      setShowUploadSigned(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload signed reminder');
    }
    setSubmitting(false);
  };

  const filteredReminders = reminders.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalCombos = reminders.length * drivers.length;
  const submittedCombos = new Set();
  const compliantCombos = new Set();
  drivers.forEach(d => {
    reminders.forEach(r => {
      const sub = getLatestSubmission(d.id, r.id);
      if (sub) {
        submittedCombos.add(`${d.id}_${r.id}`);
        const s = getStatus(sub);
        if (s.label === 'Compliant') compliantCombos.add(`${d.id}_${r.id}`);
      }
    });
  });

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Total Reminders</div>
          <div className="text-2xl font-bold" data-testid="reminder-count">{reminders.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Active Drivers</div>
          <div className="text-2xl font-bold">{drivers.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Submitted</div>
          <div className="text-2xl font-bold text-blue-600">{submittedCombos.size}/{totalCombos}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Compliant</div>
          <div className="text-2xl font-bold text-emerald-600">{compliantCombos.size}/{totalCombos}</div>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search reminders..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" data-testid="reminder-search" />
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAddReminder(true)} data-testid="add-reminder-btn"><Plus className="w-4 h-4 mr-1" />Add Reminder</Button>
        <Button size="sm" variant="outline" onClick={() => trainingAPI.downloadAllReminderTemplates()} data-testid="download-all-reminders-btn"><Download className="w-4 h-4 mr-1" />Download All</Button>
        <Button size="sm" variant="outline" onClick={() => setShowSubmissions(true)} data-testid="view-reminder-submissions-btn"><Eye className="w-4 h-4 mr-1" />View Submissions</Button>
        <Button size="sm" variant="outline" onClick={() => trainingAPI.exportRemindersExcel()} data-testid="export-reminders-btn"><Download className="w-4 h-4 mr-1" />Export Excel</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reminder Name</TableHead>
                <TableHead>Renewal</TableHead>
                <TableHead>Template</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReminders.map(r => (
                <TableRow key={r.id} data-testid={`reminder-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>{r.renewal_months} months</TableCell>
                  <TableCell>
                    {r.template_file ? (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 cursor-pointer" onClick={() => trainingAPI.downloadReminderTemplate(r.id)}>
                        <FileText className="w-3 h-3 mr-1" />{r.template_filename || 'Download'}
                      </Badge>
                    ) : (
                      <span className="text-slate-400 text-sm">No template</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50" title="Upload Signed Reminder for Driver" onClick={() => { setShowUploadSigned(r.id); setSignedFile(null); setSignedDriverId(''); }}>
                        <ClipboardCheck className="w-3.5 h-3.5 mr-1" />Signed
                      </Button>
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="outline" className="text-slate-600 border-slate-200" title="Upload/Replace Template (Admin)" onClick={() => { setShowUploadTemplate(r.id); setTemplateFile(null); }}>
                            <Upload className="w-3.5 h-3.5 mr-1" />Template
                          </Button>
                          {r.template_file && (
                            <Button size="sm" variant="ghost" title="Download Template" onClick={() => trainingAPI.downloadReminderTemplate(r.id)}>
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" title="Delete Reminder" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteReminder(r.id, r.name)} data-testid={`delete-reminder-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredReminders.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">No reminders found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Compliance Matrix */}
      {drivers.length > 0 && reminders.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Driver Compliance Matrix</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-white z-10 min-w-[160px]">Driver</TableHead>
                  {reminders.map(r => <TableHead key={r.id} className="text-center min-w-[100px] text-xs">{r.name}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="sticky left-0 bg-white z-10 font-medium text-sm">{driverName(d)}</TableCell>
                    {reminders.map(r => {
                      const sub = getLatestSubmission(d.id, r.id);
                      const st = getStatus(sub);
                      return (
                        <TableCell key={r.id} className="text-center">
                          <Badge variant={st.color === 'success' ? 'default' : st.color === 'warning' ? 'secondary' : 'destructive'} className={`text-xs ${st.color === 'success' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}`}>
                            {st.label}
                          </Badge>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Reminder Dialog */}
      <Dialog open={showAddReminder} onOpenChange={setShowAddReminder}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Safety Reminder</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Reminder Name</Label><Input value={newReminderName} onChange={e => setNewReminderName(e.target.value)} placeholder="Enter reminder name" data-testid="new-reminder-name" /></div>
          </div>
          <DialogFooter><Button onClick={handleAddReminder} disabled={submitting || !newReminderName.trim()} data-testid="save-reminder-btn">{submitting ? 'Saving...' : 'Add Reminder'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Template Dialog */}
      <Dialog open={!!showUploadTemplate} onOpenChange={() => setShowUploadTemplate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Reminder Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Template File (PDF, DOCX, etc.)</Label><Input type="file" onChange={e => setTemplateFile(e.target.files[0])} data-testid="reminder-template-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadTemplate} disabled={submitting || !templateFile} data-testid="upload-reminder-template-btn">{submitting ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Signed Reminder Dialog */}
      <Dialog open={!!showUploadSigned} onOpenChange={() => setShowUploadSigned(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Signed Reminder</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Driver</Label>
              <Select value={signedDriverId} onValueChange={setSignedDriverId}>
                <SelectTrigger data-testid="reminder-signed-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{driverName(d)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Submission Date</Label><Input type="date" value={signedDate} onChange={e => setSignedDate(e.target.value)} data-testid="reminder-signed-date-input" /></div>
            <div><Label>Signed Document (PDF, Image, etc.)</Label><Input type="file" onChange={e => setSignedFile(e.target.files[0])} data-testid="reminder-signed-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadSigned} disabled={submitting || !signedFile || !signedDriverId} data-testid="upload-reminder-signed-btn">{submitting ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions List Dialog */}
      <Dialog open={showSubmissions} onOpenChange={setShowSubmissions}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Poster Submissions</DialogTitle></DialogHeader>
          <div className="flex gap-3 mb-4">
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger className="max-w-[200px]"><SelectValue placeholder="All Drivers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map(d => <SelectItem key={d.id} value={d.id}>{driverName(d)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterReminder} onValueChange={setFilterReminder}>
              <SelectTrigger className="max-w-[200px]"><SelectValue placeholder="All Reminders" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reminders</SelectItem>
                {reminders.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Reminder</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Next Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>File</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions
                .filter(s => (!filterDriver || filterDriver === 'all' || s.driver_id === filterDriver))
                .filter(s => (!filterReminder || filterReminder === 'all' || s.reminder_id === filterReminder))
                .map(s => {
                  const st = getStatus(s);
                  return (
                    <TableRow key={s.id}>
                      <TableCell>{s.driver_name}</TableCell>
                      <TableCell>{s.reminder_name}</TableCell>
                      <TableCell>{s.submission_date?.slice(0, 10)}</TableCell>
                      <TableCell>{s.next_due_date?.slice(0, 10)}</TableCell>
                      <TableCell><Badge variant={st.color === 'success' ? 'default' : st.color === 'warning' ? 'secondary' : 'destructive'} className={st.color === 'success' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}>{st.label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" title="Download" onClick={() => trainingAPI.downloadSignedReminder(s.id)}><Download className="w-4 h-4" /></Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" title="Delete" onClick={async () => {
                              if (!window.confirm(`Delete signed reminder for ${s.driver_name}? This cannot be undone.`)) return;
                              try {
                                await trainingAPI.deleteSignedReminder(s.id);
                                toast.success('Deleted');
                                fetchData();
                              } catch { toast.error('Delete failed'); }
                            }}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Truck Safety Manual Tab ────────────────────────
function TruckSafetyManualSection() {
  const { isAdmin } = useAuth();
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedTruck, setExpandedTruck] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);
  const [confirmFile, setConfirmFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trainingAPI.listTruckManuals();
      setManuals(res.data);
    } catch (e) {
      toast.error('Failed to load truck manuals');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConfirm = async () => {
    if (!confirmFile || !showConfirm) return;
    setSubmitting(true);
    try {
      await trainingAPI.confirmTruckManualItem(
        showConfirm.truckId, showConfirm.itemType, showConfirm.itemId, confirmFile
      );
      toast.success('Confirmation uploaded');
      setConfirmFile(null);
      setShowConfirm(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload confirmation');
    }
    setSubmitting(false);
  };

  const handleReview = async (truckId) => {
    try {
      await trainingAPI.markTruckManualReviewed(truckId);
      toast.success('Manual marked as reviewed');
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to mark as reviewed';
      toast.error(detail);
    }
  };

  const filteredManuals = manuals.filter(m =>
    m.registration_number.toLowerCase().includes(search.toLowerCase())
  );

  const totalTrucks = manuals.length;
  const fullyConfirmed = manuals.filter(m => m.total_items > 0 && m.confirmed_items === m.total_items).length;
  const avgCompletion = manuals.length > 0
    ? Math.round(manuals.reduce((sum, m) => sum + (m.total_items > 0 ? m.confirmed_items / m.total_items * 100 : 0), 0) / manuals.length)
    : 0;

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Total Trucks</div>
          <div className="text-2xl font-bold" data-testid="truck-count">{totalTrucks}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Fully Confirmed</div>
          <div className="text-2xl font-bold text-emerald-600">{fullyConfirmed}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Avg Completion</div>
          <div className="text-2xl font-bold text-blue-600">{avgCompletion}%</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Need Attention</div>
          <div className="text-2xl font-bold text-amber-600">{totalTrucks - fullyConfirmed}</div>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search trucks..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" data-testid="truck-manual-search" />
        </div>
        <Button size="sm" variant="outline" onClick={() => trainingAPI.exportTruckManualsExcel()} data-testid="export-truck-manuals-btn"><Download className="w-4 h-4 mr-1" />Export Excel</Button>
      </div>

      {/* Truck Cards */}
      <div className="space-y-3">
        {filteredManuals.map(truck => {
          const isExpanded = expandedTruck === truck.truck_id;
          const pct = truck.total_items > 0 ? Math.round(truck.confirmed_items / truck.total_items * 100) : 0;
          return (
            <Card key={truck.truck_id} data-testid={`truck-manual-${truck.truck_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4 cursor-pointer" onClick={() => setExpandedTruck(isExpanded ? null : truck.truck_id)}>
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-slate-900">{truck.registration_number}</span>
                      <span className="text-sm text-slate-500">{truck.make} {truck.model}</span>
                      <Badge variant={pct === 100 ? 'default' : pct >= 50 ? 'secondary' : 'destructive'}
                        className={pct === 100 ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}>
                        {truck.confirmed_items}/{truck.total_items} confirmed
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      <span>Last Review: {truck.last_review_date ? truck.last_review_date.slice(0, 10) : 'Never'}</span>
                      <span>Next Review: {truck.next_review_date ? truck.next_review_date.slice(0, 10) : 'N/A'}</span>
                    </div>
                  </div>
                  <div className="w-32">
                    <Progress value={pct} className="h-2" />
                    <div className="text-xs text-center text-slate-500 mt-1">{pct}%</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleReview(truck.truck_id); }} data-testid={`review-truck-${truck.truck_id}`}>
                    <CheckCircle className="w-4 h-4 mr-1" />Review
                  </Button>
                </div>

                {isExpanded && (
                  <div className="mt-4 space-y-4">
                    {/* Reminders */}
                    {truck.reminders.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4" />Safety Posters</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {truck.reminders.map(r => (
                            <div key={r.reminder_id} className={`flex items-center justify-between p-2 rounded border text-sm ${r.confirmed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                              <div className="flex items-center gap-2">
                                {r.confirmed ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                                <span className="text-xs">{r.reminder_name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {r.confirmed && r.confirmation_id && (
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => trainingAPI.downloadManualConfirmation(r.confirmation_id)} title="View confirmation">
                                    <Eye className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setShowConfirm({ truckId: truck.truck_id, itemType: 'reminder', itemId: r.reminder_id, name: r.reminder_name }); setConfirmFile(null); }} title="Upload confirmation">
                                  <Camera className="w-3 h-3" />
                                </Button>
                                {r.confirmed && r.confirmation_id && isAdmin && (
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={async () => {
                                    if (!window.confirm(`Delete confirmation for "${r.reminder_name}"?`)) return;
                                    try {
                                      await trainingAPI.deleteManualConfirmation(r.confirmation_id);
                                      toast.success('Confirmation deleted');
                                      fetchData();
                                    } catch { toast.error('Delete failed'); }
                                  }} title="Delete confirmation">
                                    <Trash2 className="w-3 h-3 text-red-600" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Routes */}
                    {truck.routes.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><Shield className="w-4 h-4" />Route Risk Assessments</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {truck.routes.map(r => (
                            <div key={r.route_id} className={`flex items-center justify-between p-2 rounded border text-sm ${r.confirmed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                              <div className="flex items-center gap-2">
                                {r.confirmed ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                                <span className="text-xs">{r.route_name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {r.confirmed && r.confirmation_id && (
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => trainingAPI.downloadManualConfirmation(r.confirmation_id)} title="View confirmation">
                                    <Eye className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setShowConfirm({ truckId: truck.truck_id, itemType: 'route', itemId: r.route_id, name: r.route_name }); setConfirmFile(null); }} title="Upload confirmation">
                                  <Camera className="w-3 h-3" />
                                </Button>
                                {r.confirmed && r.confirmation_id && isAdmin && (
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={async () => {
                                    if (!window.confirm(`Delete confirmation for "${r.route_name}"?`)) return;
                                    try {
                                      await trainingAPI.deleteManualConfirmation(r.confirmation_id);
                                      toast.success('Confirmation deleted');
                                      fetchData();
                                    } catch { toast.error('Delete failed'); }
                                  }} title="Delete confirmation">
                                    <Trash2 className="w-3 h-3 text-red-600" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {filteredManuals.length === 0 && (
          <Card><CardContent className="py-12 text-center text-slate-400">No trucks found</CardContent></Card>
        )}
      </div>

      {/* Upload Confirmation Dialog */}
      <Dialog open={!!showConfirm} onOpenChange={() => setShowConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Confirmation Photo</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 mb-4">
            Upload a photo confirming <strong>{showConfirm?.name}</strong> is in the truck safety manual.
          </p>
          <div className="space-y-4">
            <div><Label>Photo/Document</Label><Input type="file" accept="image/*,.pdf" onChange={e => setConfirmFile(e.target.files[0])} data-testid="confirmation-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleConfirm} disabled={submitting || !confirmFile} data-testid="upload-confirmation-btn">{submitting ? 'Uploading...' : 'Upload Confirmation'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Training Courses Tab ────────────────────────
function TrainingCoursesSection() {
  const { isAdmin } = useAuth();
  const [courses, setCourses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showUploadResult, setShowUploadResult] = useState(false);
  const [showUploadCourseTemplate, setShowUploadCourseTemplate] = useState(null);
  const [showUploadTestTemplate, setShowUploadTestTemplate] = useState(false);
  const [courseTemplateFile, setCourseTemplateFile] = useState(null);
  const [testTemplateFile, setTestTemplateFile] = useState(null);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [filterDriver, setFilterDriver] = useState('');
  const [resultFile, setResultFile] = useState(null);
  const [resultDriverId, setResultDriverId] = useState('');
  const [resultCourseId, setResultCourseId] = useState('');
  const [resultDate, setResultDate] = useState(new Date().toISOString().split('T')[0]);
  const [resultOutcome, setResultOutcome] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [crsRes, drvRes, subRes] = await Promise.all([
        trainingAPI.listCourses(),
        driversAPI.list(),
        trainingAPI.listCourseSubmissions(),
      ]);
      setCourses(crsRes.data);
      setDrivers(drvRes.data.filter(d => d.status === 'active' || d.is_active !== false));
      setSubmissions(subRes.data);
    } catch (e) {
      toast.error('Failed to load training data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Get the latest test submission for a driver (across any course - since it's one annual training)
  const getLatestTest = (driverId) => {
    return submissions
      .filter(s => s.driver_id === driverId)
      .sort((a, b) => b.training_date.localeCompare(a.training_date))[0];
  };

  const getStatus = (sub) => {
    if (!sub) return { label: 'Not Done', color: 'destructive' };
    try {
      const nextDue = new Date(sub.next_due_date);
      const now = new Date();
      const daysLeft = Math.ceil((nextDue - now) / (1000 * 60 * 60 * 24));
      if (daysLeft < 0) return { label: 'Overdue', color: 'destructive' };
      if (daysLeft <= 30) return { label: `Due in ${daysLeft}d`, color: 'warning' };
      return { label: 'Compliant', color: 'success' };
    } catch {
      return { label: 'Unknown', color: 'secondary' };
    }
  };

  const handleUploadResult = async () => {
    if (!resultFile || !resultDriverId || !resultOutcome || !resultCourseId) return;
    setSubmitting(true);
    try {
      await trainingAPI.uploadCourseTestResult(resultCourseId, resultDriverId, resultDate, resultOutcome, resultFile);
      toast.success('Test result uploaded');
      setResultFile(null);
      setResultDriverId('');
      setResultCourseId('');
      setResultOutcome('');
      setShowUploadResult(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload test result');
    }
    setSubmitting(false);
  };

  const handleUploadCourseTemplate = async () => {
    if (!courseTemplateFile || !showUploadCourseTemplate) return;
    setSubmitting(true);
    try {
      await trainingAPI.uploadCourseTemplate(showUploadCourseTemplate, courseTemplateFile);
      toast.success('Course template uploaded');
      setCourseTemplateFile(null);
      setShowUploadCourseTemplate(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload course template');
    }
    setSubmitting(false);
  };

  const handleUploadTestTemplate = async () => {
    if (!testTemplateFile) return;
    setSubmitting(true);
    try {
      await trainingAPI.uploadTestTemplate(testTemplateFile);
      toast.success('Test template uploaded');
      setTestTemplateFile(null);
      setShowUploadTestTemplate(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to upload test template');
    }
    setSubmitting(false);
  };

  // Stats
  const trained = new Set(submissions.map(s => s.driver_id)).size;
  const passed = new Set(
    drivers.filter(d => {
      const sub = getLatestTest(d.id);
      return sub && sub.result === 'passed' && getStatus(sub).label === 'Compliant';
    }).map(d => d.id)
  ).size;
  const failed = drivers.filter(d => {
    const sub = getLatestTest(d.id);
    return sub && sub.result === 'failed';
  }).length;

  const filteredDrivers = drivers.filter(d =>
    driverName(d).toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Active Drivers</div>
          <div className="text-2xl font-bold" data-testid="course-driver-count">{drivers.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Trained</div>
          <div className="text-2xl font-bold text-blue-600">{trained}/{drivers.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Passed & Compliant</div>
          <div className="text-2xl font-bold text-emerald-600">{passed}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-sm text-slate-500">Failed</div>
          <div className="text-2xl font-bold text-red-600">{failed}</div>
        </CardContent></Card>
      </div>

      {/* Workflow */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-3">
          <div className="flex items-start gap-3 text-sm text-blue-800">
            <AlertTriangle className="w-5 h-5 mt-0.5 text-blue-500 shrink-0" />
            <div>
              <strong>Training Workflow:</strong> Download training material below &rarr; Supervisor works through documents and safety reminders with driver &rarr; Driver writes test &rarr; Supervisor marks test and uploads with pass/fail result.
              <span className="text-blue-600 ml-1">Training is due annually per driver.</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Training Materials */}
      <Card>
        <CardHeader><CardTitle className="text-base">Training Materials</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {courses.map(c => (
            <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-sm">{c.name}</span>
              </div>
              <div className="flex items-center gap-2">
                {c.template_file ? (
                  <Button size="sm" variant="outline" onClick={() => trainingAPI.downloadCourseTemplate(c.id)}>
                    <Download className="w-4 h-4 mr-1" />{c.template_filename || 'Download'}
                  </Button>
                ) : (
                  <span className="text-xs text-slate-400">No file uploaded</span>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" className="text-slate-600 border-slate-200" onClick={() => { setShowUploadCourseTemplate(c.id); setCourseTemplateFile(null); }}>
                    <Upload className="w-3.5 h-3.5 mr-1" />{c.template_file ? 'Replace' : 'Upload'}
                  </Button>
                )}
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 border-amber-200">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-sm">Training Test</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => trainingAPI.downloadTestTemplate()}>
                <Download className="w-4 h-4 mr-1" />Training Test.pdf
              </Button>
              {isAdmin && (
                <Button size="sm" variant="outline" className="text-slate-600 border-slate-200" onClick={() => { setShowUploadTestTemplate(true); setTestTemplateFile(null); }}>
                  <Upload className="w-3.5 h-3.5 mr-1" />Replace
                </Button>
              )}
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button size="sm" variant="outline" onClick={() => trainingAPI.downloadAllCourseTemplates()} data-testid="download-all-courses-btn">
              <Download className="w-4 h-4 mr-1" />Download All Materials
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search drivers..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" data-testid="course-search" />
        </div>
        <Button size="sm" onClick={() => { setShowUploadResult(true); setResultFile(null); setResultDriverId(''); setResultCourseId(courses[0]?.id || ''); setResultOutcome(''); }} data-testid="upload-test-btn">
          <Upload className="w-4 h-4 mr-1" />Upload Test Result
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowSubmissions(true)} data-testid="view-course-submissions-btn">
          <Eye className="w-4 h-4 mr-1" />View All Results
        </Button>
        <Button size="sm" variant="outline" onClick={() => trainingAPI.exportCoursesExcel()} data-testid="export-courses-btn">
          <Download className="w-4 h-4 mr-1" />Export Excel
        </Button>
      </div>

      {/* Driver Test Results Table */}
      <Card>
        <CardHeader><CardTitle className="text-base">Driver Test Results</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead className="text-center">Test Result</TableHead>
                <TableHead>Training Date</TableHead>
                <TableHead>Next Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Test File</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDrivers.map(d => {
                const sub = getLatestTest(d.id);
                const st = getStatus(sub);
                return (
                  <TableRow key={d.id} data-testid={`driver-test-row-${d.id}`}>
                    <TableCell className="font-medium">{driverName(d)}</TableCell>
                    <TableCell className="text-center">
                      {sub ? (
                        <Badge className={`${sub.result === 'passed' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-red-100 text-red-800 hover:bg-red-100'}`}>
                          {sub.result === 'passed' ? 'Passed' : 'Failed'}
                        </Badge>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>{sub?.training_date?.slice(0, 10) || '-'}</TableCell>
                    <TableCell>{sub?.next_due_date?.slice(0, 10) || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={st.color === 'success' ? 'default' : st.color === 'warning' ? 'secondary' : 'destructive'}
                        className={`text-xs ${st.color === 'success' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}`}>
                        {st.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {sub && (
                        <Button size="sm" variant="ghost" onClick={() => trainingAPI.downloadCourseTest(sub.id)} title="Download test">
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredDrivers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No drivers found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upload Test Result Dialog */}
      <Dialog open={showUploadResult} onOpenChange={setShowUploadResult}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Test Result</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Training Course</Label>
              <Select value={resultCourseId} onValueChange={setResultCourseId}>
                <SelectTrigger data-testid="result-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Driver</Label>
              <Select value={resultDriverId} onValueChange={setResultDriverId}>
                <SelectTrigger data-testid="result-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{driverName(d)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Training Date</Label><Input type="date" value={resultDate} onChange={e => setResultDate(e.target.value)} data-testid="result-date-input" /></div>
            <div>
              <Label>Test Result</Label>
              <Select value={resultOutcome} onValueChange={setResultOutcome}>
                <SelectTrigger data-testid="result-outcome-select"><SelectValue placeholder="Select result" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Completed Test Document</Label><Input type="file" onChange={e => setResultFile(e.target.files[0])} data-testid="result-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadResult} disabled={submitting || !resultFile || !resultDriverId || !resultOutcome || !resultCourseId} data-testid="upload-result-btn">{submitting ? 'Uploading...' : 'Upload Result'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Course Template Dialog (Admin Only) */}
      <Dialog open={!!showUploadCourseTemplate} onOpenChange={() => setShowUploadCourseTemplate(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Course Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Upload the training material document for this course. This will be available for supervisors and drivers to download.</p>
            <div><Label>Template File (PDF, Word)</Label><Input type="file" accept=".pdf,.doc,.docx" onChange={e => setCourseTemplateFile(e.target.files[0])} data-testid="course-template-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadCourseTemplate} disabled={submitting || !courseTemplateFile} data-testid="upload-course-template-btn">{submitting ? 'Uploading...' : 'Upload Template'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Test Template Dialog (Admin Only) */}
      <Dialog open={showUploadTestTemplate} onOpenChange={setShowUploadTestTemplate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload/Replace Training Test Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Upload the test template that drivers will complete after training. This replaces the existing test template.</p>
            <div><Label>Test Template File (PDF, Word)</Label><Input type="file" accept=".pdf,.doc,.docx" onChange={e => setTestTemplateFile(e.target.files[0])} data-testid="test-template-file-input" /></div>
          </div>
          <DialogFooter><Button onClick={handleUploadTestTemplate} disabled={submitting || !testTemplateFile} data-testid="upload-test-template-btn">{submitting ? 'Uploading...' : 'Upload Test Template'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Results Dialog */}
      <Dialog open={showSubmissions} onOpenChange={setShowSubmissions}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>All Training Test Results</DialogTitle></DialogHeader>
          <div className="flex gap-3 mb-4">
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger className="max-w-[200px]"><SelectValue placeholder="All Drivers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map(d => <SelectItem key={d.id} value={d.id}>{driverName(d)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Training Date</TableHead>
                <TableHead>Next Due</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>File</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions
                .filter(s => (!filterDriver || filterDriver === 'all' || s.driver_id === filterDriver))
                .map(s => {
                  const st = getStatus(s);
                  return (
                    <TableRow key={s.id}>
                      <TableCell>{s.driver_name}</TableCell>
                      <TableCell>{s.training_date?.slice(0, 10)}</TableCell>
                      <TableCell>{s.next_due_date?.slice(0, 10)}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${s.result === 'passed' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-red-100 text-red-800 hover:bg-red-100'}`}>
                          {s.result === 'passed' ? 'Passed' : 'Failed'}
                        </Badge>
                      </TableCell>
                      <TableCell><Badge variant={st.color === 'success' ? 'default' : st.color === 'warning' ? 'secondary' : 'destructive'} className={st.color === 'success' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}>{st.label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" title="Download" onClick={() => trainingAPI.downloadCourseTest(s.id)}><Download className="w-4 h-4" /></Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" title="Delete" onClick={async () => {
                              if (!window.confirm(`Delete test result for ${s.driver_name}? This cannot be undone.`)) return;
                              try {
                                await trainingAPI.deleteCourseSubmission(s.id);
                                toast.success('Deleted');
                                fetchData();
                              } catch { toast.error('Delete failed'); }
                            }}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Training Page ────────────────────────────
export default function TrainingPage() {
  return (
    <div data-testid="training-page">
      <div className="flex items-center gap-3 mb-6">
        <GraduationCap className="w-7 h-7 text-slate-900" />
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>TRAINING</h1>
      </div>

      <Tabs defaultValue="policies" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="policies" data-testid="tab-policies">
            <FileText className="w-4 h-4 mr-2" />Policies
          </TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">
            <BookOpen className="w-4 h-4 mr-2" />Safety Reminders
          </TabsTrigger>
          <TabsTrigger value="training" data-testid="tab-training">
            <GraduationCap className="w-4 h-4 mr-2" />Training
          </TabsTrigger>
          <TabsTrigger value="truck-manual" data-testid="tab-truck-manual">
            <Shield className="w-4 h-4 mr-2" />Truck Safety Manual
          </TabsTrigger>
        </TabsList>

        <TabsContent value="policies">
          <PoliciesSection />
        </TabsContent>
        <TabsContent value="reminders">
          <RemindersSection />
        </TabsContent>
        <TabsContent value="training">
          <TrainingCoursesSection />
        </TabsContent>
        <TabsContent value="truck-manual">
          <TruckSafetyManualSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
